/**
 * What the whole job makes, where the customer bought more than one option.
 *
 * The strip above these belongs to the estimate on screen. On a job sold as
 * one thing that is the whole answer, and this renders nothing. On a job where
 * the customer signed for two — the roof and the gutters, the house and the
 * garage — it was only ever a tab's worth of it, and the rep had to switch
 * tabs and add up in their head to learn what they had sold.
 *
 * Jonathon, 2026-09-23: "when multiple line items are selected and the
 * customer signs for them we need to know these items for the entire job and
 * for each estimate they select." So: a line per option, and the job under
 * them.
 *
 * Every figure is a `computeLadder` result handed in whole and added up by
 * `combineLadders`. Nothing here does arithmetic (CLAUDE.md rules 3 and 5),
 * and a row cannot disagree with the tab it names because it is the same
 * ladder that tab draws.
 */

import {
  TARGET_MARGIN,
  combineLadders,
  formatCents,
  formatPercent,
  type LadderTotals,
} from "@roof-hawk/core";

import { t } from "../../i18n";

/** One option on the job, named as its tab names it. */
export interface JobTotalsOption {
  estimateId: string;
  /** `estimateOptionLabels` — what the tab above calls it. */
  label: string;
  totals: LadderTotals;
}

export function JobTotals({
  options,
  openEstimateId,
}: {
  /**
   * The job's active estimates: what the customer signed for, or the single
   * one being forecast before anybody has. Not every estimate on the job —
   * the options nobody bought are not part of what the job makes.
   */
  options: readonly JobTotalsOption[];
  /** The estimate the strip above is showing, so its line says so. */
  openEstimateId?: string | null;
}) {
  // One option is what the strip above already says, twice.
  if (options.length < 2) return null;

  const job = combineLadders(options.map((option) => option.totals));
  const thin = job.margin < TARGET_MARGIN;

  return (
    <section className="jobtot" aria-label={t("The whole job")}>
      <h3>{t("The whole job")}</h3>
      <p className="sub">
        {/* Said out loud, because "the whole job" is a claim about which
            options count, and the answer is not on this screen: an option the
            customer did not buy is not on this table at all. */}
        {t(
          "Every active option on this job, added up. The figures above are the one estimate on screen.",
        )}
      </p>

      <div className="ewrap">
        <table className="etable">
          <thead>
            <tr>
              <th>{t("Option")}</th>
              <th className="num">{t("Contract")}</th>
              <th className="num">{t("Cost")}</th>
              <th className="num">{t("Gross Profit")}</th>
              <th className="num">{t("Margin")}</th>
            </tr>
          </thead>
          <tbody>
            {options.map((option) => (
              <tr key={option.estimateId}>
                <td>
                  {option.label}
                  {option.estimateId === openEstimateId && (
                    <span className="sub"> · {t("shown above")}</span>
                  )}
                </td>
                <td className="num">{formatCents(option.totals.grandTotal)}</td>
                <td className="num">{formatCents(option.totals.totalCost)}</td>
                <td
                  className={option.totals.grossProfit < 0 ? "num bad" : "num"}
                >
                  {formatCents(option.totals.grossProfit)}
                </td>
                <td className="num">{formatPercent(option.totals.margin)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row">{t("Whole job")}</th>
              <td className="num">{formatCents(job.grandTotal)}</td>
              <td className="num">{formatCents(job.totalCost)}</td>
              <td className={job.grossProfit < 0 ? "num bad" : "num"}>
                {formatCents(job.grossProfit)}
              </td>
              {/* Under the target, the same statement the strip makes — not an
                  alarm, because jobs are sold under it deliberately. */}
              <td className={thin ? "num thin" : "num"}>
                {formatPercent(job.margin)}
                <span className="agp">
                  {" "}
                  · {formatPercent(job.agpMargin)} AGP
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}
